import React, { useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import {
  ModalBackdrop,
  ModalPanel,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalSection,
  ModalSectionTitle,
  ModalGrid,
  ModalField,
  ModalLabel,
  ModalValue,
  NegativeValue,
  ChartWrap,
  TotalAdjLabel,
} from '../styles/ChargeSearchStyles';
import { palette } from '../styles/tokens';

const fmtCurrency = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '$0.00';
};

const fmtConversion = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toFixed(4) : '—';
};

// Recharts <Cell fill> is an SVG presentation attribute — var() is invalid
// there, so these stay palette literals from the JS mirror.
const COLORS = [palette['green-800'], palette['orange-500'], palette['red-600']];

export default function ChargeSearchResultModal({ data, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!data) return null;

  const chartData = [
    { name: 'Pallets Chg.',   value: parseFloat(data.PalletsChg)   || 0 },
    { name: 'Marketing Chg.', value: parseFloat(data.MarketingChg) || 0 },
    { name: 'Packing Chg.',   value: parseFloat(data.PackingChg)   || 0 },
  ];
  const allZero = chartData.every((d) => d.value === 0);

  const boxDiff = parseFloat(data.BoxDifference);
  const isNegBoxDiff = Number.isFinite(boxDiff) && boxDiff < 0;
  const targetValueDisplay = (() => {
    const n = parseFloat(data.targetValue);
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
  })();

  return (
    <ModalBackdrop onClick={onClose} role="presentation">
      <ModalPanel
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Charge Search Result Details"
      >
        <ModalHeader>
          <ModalTitle>Search Result — {data.Commodity ?? '—'} / {data.BagType ?? '—'}</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">✕</CloseButton>
        </ModalHeader>

        <ModalSection>
          <ModalSectionTitle>Style Properties</ModalSectionTitle>
          <ModalGrid>
            <ModalField>
              <ModalLabel>Commodity</ModalLabel>
              <ModalValue>{data.Commodity ?? '—'}</ModalValue>
            </ModalField>
            <ModalField>
              <ModalLabel>Bag Type</ModalLabel>
              <ModalValue>{data.BagType ?? '—'}</ModalValue>
            </ModalField>
            <ModalField>
              <ModalLabel>Product Type</ModalLabel>
              <ModalValue>{data.ProductType ?? '—'}</ModalValue>
            </ModalField>
            <ModalField>
              <ModalLabel>Ti × Hi</ModalLabel>
              <ModalValue>{data.TiHi || '—'}</ModalValue>
            </ModalField>
            <ModalField>
              <ModalLabel>Cost Conversion</ModalLabel>
              <ModalValue>{fmtConversion(data.CostConversion)}</ModalValue>
            </ModalField>
          </ModalGrid>
        </ModalSection>

        <ModalSection>
          <ModalSectionTitle>Search Result</ModalSectionTitle>
          <ModalGrid>
            <ModalField>
              <ModalLabel>Calculation Mode</ModalLabel>
              <ModalValue>{data.calculationType ?? '—'}</ModalValue>
            </ModalField>
            <ModalField>
              <ModalLabel>Target Value</ModalLabel>
              <ModalValue>{targetValueDisplay}</ModalValue>
            </ModalField>
            <ModalField>
              <ModalLabel>Total Cost</ModalLabel>
              <ModalValue>{fmtCurrency(data.TotalCost)}</ModalValue>
            </ModalField>
            <ModalField>
              <ModalLabel>Return to Grower</ModalLabel>
              <ModalValue>{fmtCurrency(data.ReturnToGrower)}</ModalValue>
            </ModalField>
            <ModalField>
              <ModalLabel>Packaging Method</ModalLabel>
              <ModalValue>{data.MethodName ?? '—'}</ModalValue>
            </ModalField>
            <ModalField>
              <ModalLabel>Box Difference</ModalLabel>
              <ModalValue>
                {isNegBoxDiff
                  ? <NegativeValue>{fmtCurrency(boxDiff)}</NegativeValue>
                  : fmtCurrency(boxDiff)}
              </ModalValue>
            </ModalField>
          </ModalGrid>
        </ModalSection>

        <ModalSection>
          <ModalSectionTitle>Charge Breakdown</ModalSectionTitle>
          {allZero ? (
            <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--ink-a60)', fontSize: '0.88rem' }}>
              No non-zero charge components to chart for this style.
            </div>
          ) : (
            <ChartWrap>
              <PieChart width={300} height={260}>
                <Pie data={chartData} cx={150} cy={120} outerRadius={100} dataKey="value">
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                <Legend />
              </PieChart>
            </ChartWrap>
          )}
          <TotalAdjLabel style={{ textAlign: 'center', marginTop: 6 }}>
            Total Adj.<span>{fmtCurrency(data.TotalChgAdjustment)}</span>
          </TotalAdjLabel>
        </ModalSection>
      </ModalPanel>
    </ModalBackdrop>
  );
}
